import { useMemo } from 'react';

import { useMerchant } from '@contexts/operator/MerchantContext';

import operatorApiFetch from '@services/operator/apiService';

/**
 * Operator Order Template Service Hook
 *
 * Receives merchantId as parameter and injects it into all API calls
 *
 * @param {Object} [options] - Options
 * @param {string} [options.merchantId] - Merchant ID (optional, if not provided will use MerchantContext)
 *
 * @example
 * const orderTemplateService = useOperatorOrderTemplateService({ merchantId: 123 });
 *
 * // merchant_id will be auto-injected to request
 * await orderTemplateService.create({ name: 'Template A', content: '...' });
 * await orderTemplateService.getById(1);
 */
export function useOperatorOrderTemplateService(options = {}) {
  const merchantContext = useMerchant();
  const contextMerchantId = merchantContext?.merchantId;

  const merchantId = options.merchantId ?? contextMerchantId;  // Passed in takes priority, otherwise use context

  return useMemo(() => ({
    // Get order templates list (for search) - Alias for fetchList
    index: async (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.limit) queryParams.append('limit', params.limit);
      if (merchantId) queryParams.append('merchant_id', merchantId);

      const res = await operatorApiFetch(`order_templates?${queryParams.toString()}`, {
        method: 'GET'
      });
      return res.json();
    },

    // Get order templates list (for search)
    fetchList: async (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.limit) queryParams.append('limit', params.limit);
      if (merchantId) queryParams.append('merchant_id', merchantId);

      const res = await operatorApiFetch(`order_templates?${queryParams.toString()}`, {
        method: 'GET'
      });
      return res.json();
    },

    // Get order templates list (for table)
    getRecords: async (params = {}) => {
      const res = await operatorApiFetch('order_templates/get_records', {
        method: 'POST',
        body: {
          ...params,
          merchant_id: merchantId
        }
      });
      return res.json();
    },

    // Get all order template IDs (for filtering)
    getIds: async (params = {}) => {
      const res = await operatorApiFetch('order_templates/get_ids', {
        method: 'POST',
        body: {
          ...params,
          merchant_id: merchantId
        }
      });
      return res.json();
    },

    // Get single order template
    get: async (id) => {
      const res = await operatorApiFetch(`order_templates/${id}`, {
        method: 'GET'
      });
      return res.json();
    },

    // Get single order template (alias)
    getById: async (id) => {
      const res = await operatorApiFetch(`order_templates/${id}`, {
        method: 'GET'
      });
      return res.json();
    },

    // Create order template (auto-inject merchant_id)
    create: async (data) => {
      const res = await operatorApiFetch('order_templates', {
        method: 'POST',
        body: {
          merchant_id: merchantId,
          order_template: {
            ...data,
          }
        }
      });
      return res.json();
    },

    // Update order template
    update: async (id, data) => {
      const res = await operatorApiFetch(`order_templates/${id}`, {
        method: 'PUT',
        body: {
          order_template: {
            ...data,
          }
        }
      });
      return res.json();
    },

    // Delete order template
    delete: async (id) => {
      const res = await operatorApiFetch(`order_templates/${id}`, {
        method: 'DELETE'
      });
      return res.json();
    },

    // Get merchant channels list
    getSalesChannels: async () => {
      const url = merchantId
        ? `sales_channels?merchant_id=${merchantId}`
        : 'sales_channels';
      const res = await operatorApiFetch(url, {
        method: 'GET'
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        return data.data.map(channel => ({
          label: channel.name,
          value: channel.id
        }));
      }
      return [];
    },

    // Get default liquid template
    getDefaultTemplate: async (locale = 'zh') => {
      const url = `order_templates/default_template?locale=${locale}`;
      const res = await operatorApiFetch(url, {
        method: 'GET'
      });
      return res.json();
    },

    // Upload PDF and analyze with AI
    analyzePdf: async (formData) => {
      // Note: FormData automatically sets correct Content-Type with boundary
      const res = await operatorApiFetch('order_templates/analyze_pdf', {
        method: 'POST',
        body: formData  // FormData will be detected automatically
      });
      return res.json();
    },

    // Get sample reserved barcode area
    getSampleReservedBarcodeArea: async (locale = 'zh') => {
      const url = `order_templates/sample_reserved_barcode_area?locale=${locale}`;
      const res = await operatorApiFetch(url, {
        method: 'GET'
      });
      return res.json();
    }
  }), [merchantId]);
}
